import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CircleNotch, Crop, MagicWand } from "@phosphor-icons/react";

const DEFAULT = [[0.08, 0.08], [0.92, 0.08], [0.92, 0.92], [0.08, 0.92]];
const clamp = (v) => Math.min(1, Math.max(0, v));

// Draggable 4-corner editor (mouse + touch). Corners are normalized 0..1 in image space,
// so they map 1:1 onto the existing POST /api/scan/perspective contract.
export default function CornerEditor({ src, onApply, onClose }) {
  const [corners, setCorners] = useState(DEFAULT);
  const [detecting, setDetecting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [dims, setDims] = useState({ w: 4, h: 3 });
  const svgRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setDims({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = src;
    detect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const detect = async () => {
    setDetecting(true);
    try {
      const { data } = await api.post("/scan/detect", { image: src });
      if (data.found && data.corners) { setCorners(data.corners); toast.success("Edges detected — drag any corner to fine-tune"); }
      else toast.info("No clear edges found — position the corners manually");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Edge detection failed"); }
    finally { setDetecting(false); }
  };

  const toNorm = (evt) => {
    const r = svgRef.current.getBoundingClientRect();
    return [clamp((evt.clientX - r.left) / r.width), clamp((evt.clientY - r.top) / r.height)];
  };
  const onDown = (i) => (e) => { e.preventDefault(); dragRef.current = i; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onMove = (e) => {
    if (dragRef.current === null || dragRef.current === undefined) return;
    const p = toNorm(e);
    setCorners((c) => c.map((pt, j) => (j === dragRef.current ? p : pt)));
  };
  const onUp = () => { dragRef.current = null; };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/scan/perspective", { image: src, corners });
      if (!data.found) { toast.error("Corners are too close together — spread them out"); return; }
      onApply(data.image);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not straighten this page"); }
    finally { setApplying(false); }
  };

  const pts = corners.map(([x, y]) => `${x * dims.w},${y * dims.h}`).join(" ");
  const handleR = Math.max(dims.w, dims.h) * 0.018;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="corner-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Crop size={18} className="text-primary" /> Adjust document corners</DialogTitle>
          <DialogDescription>Drag the four gold handles onto the document's corners, then apply to straighten the page.</DialogDescription>
        </DialogHeader>
        <div className="relative rounded-xl overflow-hidden bg-black/70 select-none" style={{ touchAction: "none" }}>
          <img src={src} alt="Page to adjust" className="w-full max-h-[60vh] object-contain block" draggable={false} data-testid="corner-editor-image" />
          <svg ref={svgRef} viewBox={`0 0 ${dims.w} ${dims.h}`} preserveAspectRatio="xMidYMid meet"
            className="absolute inset-0 w-full h-full" onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} data-testid="corner-editor-svg">
            <polygon points={pts} fill="rgba(201,165,92,0.18)" stroke="#c9a55c" strokeWidth={handleR * 0.25} />
            {corners.map(([x, y], i) => (
              <circle key={i} cx={x * dims.w} cy={y * dims.h} r={handleR} fill="#c9a55c" stroke="#20242a" strokeWidth={handleR * 0.2}
                style={{ cursor: "grab" }} onPointerDown={onDown(i)} data-testid={`corner-handle-${i}`} />
            ))}
          </svg>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={detect} disabled={detecting} data-testid="corner-detect">{detecting ? <CircleNotch size={15} className="mr-1 animate-spin" /> : <MagicWand size={15} className="mr-1" />} Detect edges</Button>
          <Button variant="ghost" onClick={() => setCorners(DEFAULT)} data-testid="corner-reset">Reset</Button>
          <Button variant="outline" onClick={onClose} data-testid="corner-cancel">Cancel</Button>
          <Button onClick={apply} disabled={applying} className="rounded-full" data-testid="corner-apply">{applying ? <CircleNotch size={15} className="mr-1 animate-spin" /> : <Crop size={15} className="mr-1" />} Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
